<template>
  <div class="section">
    <div class="container">
      <div class="page-header">
        <h1 class="page-title">รายการสั่งซื้อของฉัน</h1>
        <button @click="logout" class="btn btn-ghost logout-btn">
          ออกจากระบบ
        </button>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center" style="padding: 3rem 0;">
        <div class="loading-spinner" style="margin: 0 auto;"></div>
      </div>

      <!-- Empty -->
      <div v-else-if="orders.length === 0" class="empty-state">
        <svg class="empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
        </svg>
        <h3 class="empty-title">ยังไม่มีรายการสั่งซื้อ</h3>
        <p class="empty-desc">คุณยังไม่มีรายการสั่งซื้อ เริ่มช้อปปิ้งเลย!</p>
        <router-link to="/products" class="btn btn-primary">
          เริ่มช้อปปิ้ง
        </router-link>
      </div>

      <!-- Orders List -->
      <div v-else class="orders-list">
        <div v-for="order in orders" :key="order.id" class="order-card card">
          <div class="card-body">
            <div class="order-header">
              <div>
                <div class="order-number">#{{ order.order_number }}</div>
                <p class="order-date">{{ formatDate(order.created_at) }}</p>
              </div>
              <span class="badge" :class="getStatusClass(order.status)">
                {{ getStatusText(order.status) }}
              </span>
            </div>

            <div class="order-summary">
              <div class="order-total">{{ formatPrice(order.total_amount) }}</div>
              <p class="order-items-count">{{ order.items?.length || 0 }} รายการ</p>
            </div>

            <div class="order-actions">
              <router-link 
                v-if="order.status === 'pending_payment'"
                :to="`/order/${order.order_number}`"
                class="btn btn-primary btn-sm"
              >
                ชำระเงิน
              </router-link>
              
              <router-link 
                :to="`/tracking/${order.order_number}`"
                class="btn btn-outline btn-sm"
              >
                ดูรายละเอียด
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import api from '../api';
import { useAuthStore } from '../stores/auth';
import { getOrderStatusLabel } from '../utils/orderStatus';

const router = useRouter();
const authStore = useAuthStore();
const orders = ref([]);
const loading = ref(true);

onMounted(async () => {
  try {
    const response = await api.get('/member/orders');
    orders.value = response.data.data.data || response.data.data;
  } catch (error) {
    console.error('Failed to fetch orders', error);
  } finally {
    loading.value = false;
  }
});

const formatPrice = (price) => {
  return new Intl.NumberFormat('th-TH', { style: 'currency', currency: 'THB' }).format(price);
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleString('th-TH');
};

const getStatusClass = (status) => {
  const classes = {
    'pending_payment': 'badge-warning',
    'pending_verification': 'badge-warning',
    'paid': 'badge-success',
    'processing': 'badge-warning',
    'shipped': 'badge-success',
    'delivered': 'badge-success',
    'cancelled': 'badge-danger'
  };
  return classes[status] || 'badge-warning';
};

const getStatusText = (status) => getOrderStatusLabel(status);

const logout = async () => {
  await authStore.logout();
  router.push('/login');
};
</script>

<style scoped>
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.page-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--color-text);
}

.logout-btn {
  color: #dc2626;
}

.logout-btn:hover {
  background: #fee2e2;
}

.empty-state {
  text-align: center;
  padding: 4rem 1rem;
  background: var(--color-surface);
  border-radius: 0.75rem;
}

.empty-icon {
  width: 4rem;
  height: 4rem;
  margin: 0 auto 1rem;
  color: var(--color-text-muted);
}

.empty-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 0.5rem;
}

.empty-desc {
  color: var(--color-text-light);
  margin-bottom: 1.5rem;
}

.orders-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.order-card {
  transition: box-shadow 0.2s ease;
}

.order-card:hover {
  box-shadow: var(--shadow-md);
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--color-border-light);
}

.order-number {
  font-weight: 700;
  font-size: 1.125rem;
  color: var(--color-primary);
}

.order-date {
  font-size: 0.875rem;
  color: var(--color-text-muted);
}

.order-summary {
  margin-bottom: 1rem;
}

.order-total {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-text);
}

.order-items-count {
  font-size: 0.875rem;
  color: var(--color-text-muted);
}

.order-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
}
</style>
